/**
 * <deferred-media>
 * Lazy-loads a <template> child (video or iframe) on first play-button click,
 * then plays it. Host-aware play/pause via postMessage for YouTube/Vimeo.
 */
class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    this.loaded = false;
  }

  connectedCallback() {
    const poster = this.querySelector('.deferred-media__poster');
    if (poster) {
      poster.addEventListener('click', () => this.play());
    }

    const toggle = this.querySelector('.deferred-media__toggle');
    if (toggle) {
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        this.userPaused = this.dataset.playing === 'true';
        if (this.userPaused) {
          this.pause();
        } else {
          this.play();
        }
      });
    }

    this.attachVideoStateListeners();
    this.bindControlsActivity();

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!this.userPaused) this.play();
          } else {
            this.pause();
          }
        });
      }, { threshold: 0.5 });
      this.observer.observe(this);
    }
  }

  attachVideoStateListeners() {
    const video = this.querySelector('video');
    if (!video || video.dataset.stateBound === 'true') return;
    video.dataset.stateBound = 'true';
    video.addEventListener('play', () => this.setPlayingState(true));
    video.addEventListener('playing', () => this.setPlayingState(true));
    video.addEventListener('pause', () => this.setPlayingState(false));
    video.addEventListener('ended', () => this.setPlayingState(false));
  }

  setPlayingState(playing) {
    this.dataset.playing = playing ? 'true' : 'false';
    const toggle = this.querySelector('.deferred-media__toggle');
    if (toggle) {
      toggle.setAttribute('aria-pressed', playing ? 'true' : 'false');
      toggle.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    }
    if (playing) {
      this.scheduleControlsHide();
    } else {
      this.showControls();
    }
  }

  showControls() {
    this.dataset.controls = 'visible';
    if (this.hideControlsTimer) {
      clearTimeout(this.hideControlsTimer);
      this.hideControlsTimer = null;
    }
  }

  scheduleControlsHide(delay = 2000) {
    if (this.hideControlsTimer) clearTimeout(this.hideControlsTimer);
    this.dataset.controls = 'visible';
    this.hideControlsTimer = setTimeout(() => {
      if (this.dataset.playing === 'true') {
        this.dataset.controls = 'hidden';
      }
    }, delay);
  }

  bindControlsActivity() {
    if (this.activityBound) return;
    this.activityBound = true;
    const wake = () => {
      if (this.dataset.playing === 'true') {
        this.scheduleControlsHide();
      } else {
        this.showControls();
      }
    };
    this.addEventListener('pointermove', wake);
    this.addEventListener('pointerdown', wake);
    this.addEventListener('touchstart', wake, { passive: true });
    this.addEventListener('focusin', wake);
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }

  loadContent() {
    if (this.loaded) return;
    const template = this.querySelector('template');
    if (template) {
      const content = document.createElement('div');
      content.className = 'deferred-media__content';
      content.appendChild(template.content.firstElementChild.cloneNode(true));
      this.appendChild(content);
    }
    this.dataset.loaded = 'true';
    this.loaded = true;
  }

  getPlayer() {
    return this.querySelector('video, iframe');
  }

  play() {
    this.loadContent();
    this.attachVideoStateListeners();
    const player = this.getPlayer();
    if (!player) return;

    if (player.tagName === 'VIDEO') {
      const promise = player.play();
      if (promise && typeof promise.catch === 'function') promise.catch(() => {});
      return;
    }

    this.setPlayingState(true);

    // iframe — host-aware postMessage
    const host = this.dataset.videoHost;
    // Wait for iframe to load before posting
    const post = () => {
      try {
        if (host === 'youtube') {
          player.contentWindow.postMessage(
            JSON.stringify({ event: 'command', func: 'playVideo', args: '' }),
            '*'
          );
        } else if (host === 'vimeo') {
          player.contentWindow.postMessage(
            JSON.stringify({ method: 'play' }),
            '*'
          );
        }
      } catch (e) {
        /* noop */
      }
    };
    if (player.contentWindow && player.dataset.ready === 'true') {
      post();
    } else {
      player.addEventListener('load', () => {
        player.dataset.ready = 'true';
        post();
      }, { once: true });
    }
  }

  pause() {
    const player = this.getPlayer();
    if (!player) return;

    if (player.tagName === 'VIDEO') {
      player.pause();
      return;
    }

    const host = this.dataset.videoHost;
    try {
      if (host === 'youtube') {
        player.contentWindow.postMessage(
          JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }),
          '*'
        );
      } else if (host === 'vimeo') {
        player.contentWindow.postMessage(
          JSON.stringify({ method: 'pause' }),
          '*'
        );
      }
    } catch (e) {
      /* noop */
    }

    this.setPlayingState(false);
  }
}

customElements.define('deferred-media', DeferredMedia);
